import React, {useContext, useMemo, useState} from 'react';
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalTitle
} from 'react-bootstrap';
import Switch from 'react-bootstrap-switch';

import {ApiContext} from '../../../../api/Api';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {NotifierContext} from '../../../../utils/Notifier';

const EmployeeCorporateContactFormModal = ({
    employeeId,
    contact,
    onHide,
    onSaved
}) => {
    const api = useContext(ApiContext);
    const notifier = useContext(NotifierContext);

    const editing = !!contact;

    const [type, setType] = useState(
        contact ? contact.type : 'EMAIL'
    );

    const [label, setLabel] = useState(
        contact ? contact.label : ''
    );

    const [value, setValue] = useState(
        contact ? contact.value : ''
    );

    const [isPrimary, setIsPrimary] = useState(
        contact ? contact.isPrimary : false
    );

    const [showOnBusinessCard, setShowOnBusinessCard] = useState(
        contact ? contact.showOnBusinessCard : true
    );

    const [sortOrder, setSortOrder] = useState(
        contact ? contact.sortOrder : 10
    );

    const [saving, setSaving] = useState(false);

    const valueLabel = useMemo(
        () => type === 'EMAIL'
            ? 'Dirección de correo corporativo'
            : 'Número de teléfono empresarial',
        [type]
    );

    const valuePlaceholder = useMemo(
        () => type === 'EMAIL'
            ? 'nombre@tgle.mx'
            : '+52 55 0000 0000',
        [type]
    );

    const save = () => {
        const cleanLabel = label.trim();
        const cleanValue = value.trim();

        if (!cleanLabel) {
            return notifier.error(
                'Debes indicar una etiqueta para el contacto.'
            );
        }

        if (!cleanValue) {
            return notifier.error(
                type === 'EMAIL'
                    ? 'Debes indicar el correo corporativo.'
                    : 'Debes indicar el teléfono empresarial.'
            );
        }

        if (
            type === 'EMAIL'
            && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanValue)
        ) {
            return notifier.error(
                'La dirección de correo corporativo no es válida.'
            );
        }

        const numericSortOrder = Number(sortOrder);

        if (
            !Number.isInteger(numericSortOrder)
            || numericSortOrder < 0
        ) {
            return notifier.error(
                'El orden debe ser un número entero igual o mayor a cero.'
            );
        }

        const employeeCorporateContact = {
            employee: `/api/employees/${employeeId}`,
            type,
            label: cleanLabel,
            value: cleanValue,
            isPrimary,
            showOnBusinessCard,
            sortOrder: numericSortOrder
        };

        setSaving(true);

        const request = editing
            ? api.employeeCorporateContacts.edit({
                id: contact.id,
                employeeCorporateContact,
                loadingId:
                    '@EmployeeCorporateContactFormModal.edit'
            })
            : api.employeeCorporateContacts.create({
                employeeCorporateContact,
                loadingId:
                    '@EmployeeCorporateContactFormModal.create'
            });

        request
            .then(() => {
                setSaving(false);

                if (onSaved) {
                    onSaved();
                }

                onHide();
            })
            .catch(() => {
                setSaving(false);
            });
    };

    return (
        <Modal
            show={true}
            onHide={saving ? undefined : onHide}
            backdrop={saving ? 'static' : true}
            className="EmployeeCorporateContactFormModal"
        >
            <ModalHeader
                closeButton={!saving}
                onHide={saving ? undefined : onHide}
            >
                <ModalTitle>
                    {editing
                        ? 'Editar contacto corporativo'
                        : 'Nuevo contacto corporativo'}
                </ModalTitle>
            </ModalHeader>

            <ModalBody>
                <div className="form-group">
                    <label>Tipo de contacto *</label>

                    <select
                        className="form-control"
                        value={type}
                        disabled={saving}
                        onChange={event => setType(event.target.value)}
                    >
                        <option value="EMAIL">
                            Correo corporativo
                        </option>

                        <option value="PHONE">
                            Teléfono empresarial
                        </option>
                    </select>
                </div>

                <div className="form-group">
                    <label>Etiqueta *</label>

                    <input
                        type="text"
                        className="form-control"
                        value={label}
                        maxLength={100}
                        disabled={saving}
                        placeholder={
                            type === 'EMAIL'
                                ? 'Ej. Correo principal, Tecnología'
                                : 'Ej. Oficina, Celular corporativo'
                        }
                        onChange={event => setLabel(event.target.value)}
                    />

                    <p
                        className="help-block"
                        style={{marginBottom: 0}}
                    >
                        Nombre con el que se identificará este dato.
                    </p>
                </div>

                <div className="form-group">
                    <label>{valueLabel} *</label>

                    <input
                        type={type === 'EMAIL' ? 'email' : 'text'}
                        className="form-control"
                        value={value}
                        maxLength={255}
                        disabled={saving}
                        placeholder={valuePlaceholder}
                        onChange={event => setValue(event.target.value)}
                    />
                </div>

                <div className="form-group">
                    <label>Prioridad de aparición</label>

                    <input
                        type="number"
                        className="form-control"
                        value={sortOrder}
                        min="0"
                        step="1"
                        disabled={saving}
                        onChange={event => setSortOrder(event.target.value)}
                    />

                    <p
                        className="help-block"
                        style={{
                            marginBottom: 0,
                            fontSize: '12px'
                        }}
                    >
                        Los números menores aparecen primero.
                        {' '}Ejemplo: 0 antes que 1, y 1 antes que 10.
                    </p>
                </div>

                <div
                    style={{
                        borderTop: '1px solid #e8ebef',
                        paddingTop: '16px',
                        marginTop: '18px'
                    }}
                >
                    <ContactSwitch
                        label="Contacto principal"
                        help={
                            'Será el contacto preferente de este tipo. Si no seleccionas uno como principal, se utilizará el de mayor prioridad de aparición.'
                        }
                        value={isPrimary}
                        disabled={saving}
                        onChange={setIsPrimary}
                    />

                    <ContactSwitch
                        label="Mostrar en tarjeta digital"
                        help={
                            'Permitirá publicar el dato en la tarjeta corporativa.'
                        }
                        value={showOnBusinessCard}
                        disabled={saving}
                        onChange={setShowOnBusinessCard}
                    />


                </div>
            </ModalBody>

            <ModalFooter>
                <CustomButton
                    bsStyle="default"
                    disabled={saving}
                    onClick={onHide}
                >
                    Cancelar
                </CustomButton>

                <CustomButton
                    bsStyle="primary"
                    disabled={saving}
                    onClick={save}
                >
                    <i
                        className={
                            saving
                                ? 'fa fa-spinner fa-spin'
                                : 'fa fa-save'
                        }
                        style={{marginRight: '7px'}}
                    />

                    {saving
                        ? 'Guardando...'
                        : editing
                            ? 'Guardar cambios'
                            : 'Guardar contacto'}
                </CustomButton>
            </ModalFooter>
        </Modal>
    );
};

const ContactSwitch = ({
    label,
    help,
    value,
    disabled,
    onChange
}) => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 0'
        }}
    >
        <div style={{paddingRight: '20px'}}>
            <strong>{label}</strong>

            <p
                className="text-muted"
                style={{
                    margin: '3px 0 0',
                    fontSize: '12px'
                }}
            >
                {help}
            </p>
        </div>

        <Switch
            value={value}
            disabled={disabled}
            onText="Sí"
            offText="No"
            onChange={(element, nextValue) => onChange(nextValue)}
        />
    </div>
);

export default EmployeeCorporateContactFormModal;
